import React, { memo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDebouncedCallback } from 'use-debounce';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Field from './Field';
import Constants from '../../utils/Constants';
import { extendedStateType } from '../../types';
import { resolveValue } from '../../utils/fields';
import { NO_STATE } from '../../hooks/useExtendedState';
import './_styles_/RecipientsField.css';

const RECIPIENTS_SEPARATOR = ', ';

const splitRecipients = (value) => {
    if (!value) {
        return [''];
    }
    return value.split(',').map((entry) => entry.trim());
};

const joinRecipients = (entries) => entries
    .map((entry) => entry.value.trim())
    .filter((entry) => entry.length > 0)
    .join(RECIPIENTS_SEPARATOR);

RecipientsField.propTypes = {
    disabled: PropTypes.bool,
    label: PropTypes.node,
    inputId: PropTypes.string,
    onChange: PropTypes.func,
    validator: PropTypes.func,
    onValidated: PropTypes.func,
    className: PropTypes.string,
    wrapperClassName: PropTypes.string,
    placeholder: PropTypes.string,
    maxLength: PropTypes.number,
    isRequired: PropTypes.bool,
    addRecipientLabel: PropTypes.string,
    removeRecipientLabel: PropTypes.string,
    state: extendedStateType,
};

function RecipientsField(props) {
    const {
        disabled = false,
        label = null,
        inputId = null,
        onChange = undefined,
        validator = null,
        onValidated = undefined,
        className = '',
        wrapperClassName = '',
        placeholder = null,
        maxLength = null,
        isRequired = false,
        addRecipientLabel = null,
        removeRecipientLabel = null,
        state = NO_STATE,
    } = props;
    const resolvedValue = resolveValue(undefined, state);
    const nextEntryId = useRef(0);
    const createEntry = (value) => ({ id: nextEntryId.current++, value });
    const [entries, setEntries] = useState(() => splitRecipients(resolvedValue).map(createEntry));
    const [error, setError] = useState(null);
    const stateValueSetter = state && state !== NO_STATE ? state.setValueIfNew : () => {};

    const onValidationComplete = (newError, sanitizedValue) => {
        onValidated?.(sanitizedValue, validator === null ? null : !newError);
        setError(newError);
    };
    const debouncedValidation = useDebouncedCallback((newValue) => {
        validator?.(newValue)
            .then(() => onValidationComplete(null, newValue))
            .catch((e) => onValidationComplete(e, newValue));
    }, Constants.DISPLAY_FIELD_ERROR_DELAY_IN_MS);

    const applyEntries = (newEntries) => {
        setEntries(newEntries);
        const serialized = joinRecipients(newEntries);
        stateValueSetter(serialized);
        onChange?.(serialized);
        if (validator) {
            debouncedValidation(serialized);
        } else {
            onValidationComplete(null, serialized);
        }
    };

    const handleEntryChange = (id, value) => {
        applyEntries(entries.map((entry) => (entry.id === id ? { ...entry, value } : entry)));
    };
    const handleAddEntry = () => {
        applyEntries([...entries, createEntry('')]);
    };
    const handleRemoveEntry = (id) => {
        const remaining = entries.filter((entry) => entry.id !== id);
        applyEntries(remaining.length > 0 ? remaining : [createEntry('')]);
    };

    const serializedValue = joinRecipients(entries);
    const isRemoveDisabled = disabled || (entries.length === 1 && !entries[0].value);
    return (
        <Field
            label={label}
            className={['field--multiline', wrapperClassName].filter(Boolean).join(' ')}
            inputId={inputId}
            error={error}
            state={state}
            isRequired={isRequired}
            isRequiredEmpty={isRequired && !serializedValue}
        >
            <div className={`recipients-field ${className}`.trim()}>
                {entries.map((entry, index) => (
                    <div className="recipients-field--entry" key={entry.id}>
                        <input
                            id={index === 0 ? inputId : `${inputId}-${index}`}
                            className={`recipients-field--entry-input ${index === 0 ? inputId : {inputId}-{index}}`}
                            type="text"
                            value={entry.value}
                            onChange={(event) => handleEntryChange(entry.id, event.target.value)}
                            placeholder={placeholder}
                            maxLength={maxLength}
                            aria-required={isRequired}
                            disabled={disabled}
                        />
                        <button
                            type="button"
                            className="recipients-field--remove button"
                            onClick={() => handleRemoveEntry(entry.id)}
                            disabled={isRemoveDisabled}
                            aria-label={removeRecipientLabel}
                            title={removeRecipientLabel}
                        >
                            <FontAwesomeIcon icon="times" />
                        </button>
                    </div>
                ))}
                <button
                    type="button"
                    className="recipients-field--add button button-bordered"
                    onClick={handleAddEntry}
                    disabled={disabled}
                >
                    <FontAwesomeIcon icon="plus" />&nbsp;{addRecipientLabel}
                </button>
            </div>
        </Field>
    );
}

export default memo(RecipientsField);
